import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchTournaments } from "../../redux/admin/tournaments.thunk";
import { selectTournamentList } from "../../redux/admin/tournaments.selector";
import { fetchProfitLoss } from "../../redux/admin/profitLoss.thunk";
import {
  selectProfitLossData,
  selectProfitLossSummary,
  selectProfitLossLoading,
} from "../../redux/admin/profitLoss.selector";

/* ─────────────────────────────────────────────
   INLINE SVG ICONS
───────────────────────────────────────────── */
const Ico = ({ d, size = 16, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" style={style}>
    <path d={d} />
  </svg>
);
const DownloadIco = (p) => <Ico {...p} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />;
const TrendUpIco = (p) => <Ico {...p} d="M23 6l-9.5 9.5-5-5L1 18M17 6h6v6" />;
const TrendDownIco = (p) => <Ico {...p} d="M23 18l-9.5-9.5-5 5L1 6M17 18h6v-6" />;
const ScaleIco = (p) => <Ico {...p} d="M12 3v18M3 9l9-6 9 6M3 15l9 6 9-6" />;
const ChevronUpIco = (p) => <Ico {...p} d="m18 15-6-6-6 6" />;
const ChevronDownIco = (p) => <Ico {...p} d="m6 9 6 6 6-6" />;

/* ─────────────────────────────────────────────
   DESIGN TOKENS
───────────────────────────────────────────── */
const T = {
  bg: "#ffffff",
  bgSub: "#f8fafc",
  bgTertiary: "#f1f5f9",
  border: "#e2e8f0",
  borderMid: "#cbd5e1",
  textPrimary: "#0f172a",
  textSec: "#475569",
  textMuted: "#94a3b8",
  blue: "#1d4ed8",
  blueLight: "#eff6ff",
  blueBorder: "#bfdbfe",
  green: "#15803d",
  greenLight: "#f0fdf4",
  greenBorder: "#bbf7d0",
  greenDark: "#14532d",
  red: "#dc2626",
  redLight: "#fef2f2",
  redBorder: "#fecaca",
  redDark: "#7f1d1d",
  amber: "#92400e",
  amberLight: "#fffbeb",
  amberBorder: "#fde68a",
  r: 8,
  rLg: 12,
  shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)",
};

/* ─────────────────────────────────────────────
   STAT CARD
───────────────────────────────────────────── */
const StatCard = ({ label, value, icon, color, bg, border }) => (
  <div style={{
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: T.rLg,
    padding: "1rem 1.25rem",
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: "1 1 140px",
    minWidth: 0,
  }}>
    <div style={{
      width: 38,
      height: 38,
      borderRadius: T.r,
      background: T.bg,
      border: `1px solid ${border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color,
      flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <p style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 3px" }}>
        {label}
      </p>
      <p style={{ fontSize: 18, fontWeight: 700, color, margin: 0, letterSpacing: "-0.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {value}
      </p>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   TYPE PILL
───────────────────────────────────────────── */
const TypePill = ({ type }) => {
  const t = (type || "").toLowerCase();
  let bg = T.bgTertiary, color = T.textSec, border = T.border;
  if (t.includes("income") || t.includes("entry") || t.includes("fee") || t.includes("credit")) {
    bg = T.greenLight; color = T.green; border = T.greenBorder;
  } else if (t.includes("expense") || t.includes("debit") || t.includes("cost")) {
    bg = T.redLight; color = T.red; border = T.redBorder;
  } else if (t.includes("prize") || t.includes("award")) {
    bg = T.amberLight; color = T.amber; border = T.amberBorder;
  } else if (t.includes("sponsor") || t.includes("grant")) {
    bg = T.blueLight; color = T.blue; border = T.blueBorder;
  }
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      padding: "3px 8px",
      borderRadius: 4,
      background: bg,
      color,
      border: `1px solid ${border}`,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      whiteSpace: "nowrap",
    }}>
      {type}
    </span>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const ProfitLoss = () => {
  const dispatch = useDispatch();

  const tournaments = useSelector(selectTournamentList) || [];
  const data        = useSelector(selectProfitLossData) || [];
  const summary     = useSelector(selectProfitLossSummary);
  const loading     = useSelector(selectProfitLossLoading);

  const [tournamentId, setTournamentId] = useState("");
  const [sortCol, setSortCol]   = useState(null);   // null | "name" | "income" | "expense"
  const [sortDir, setSortDir]   = useState("asc");  // "asc" | "desc"
  const [typeFilter, setTypeFilter] = useState(""); // "" | unique type string

  useEffect(() => { dispatch(fetchTournaments()); }, [dispatch]);

  const loadData = useCallback(() => {
    if (!tournamentId) return;
    dispatch(fetchProfitLoss({ tournament_id: tournamentId }));
  }, [dispatch, tournamentId]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Sort & filter ── */
  const uniqueTypes = [...new Set(data.map((d) => d.type).filter(Boolean))];

  const displayData = [...data]
    .filter((item) => !typeFilter || item.type === typeFilter)
    .sort((a, b) => {
      if (!sortCol) return 0;
      let av = sortCol === "name" ? (a.name || "") : parseFloat(a[sortCol] || 0);
      let bv = sortCol === "name" ? (b.name || "") : parseFloat(b[sortCol] || 0);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  };

  /* ── Export ── */
  const handleExportExcel = () => {
    if (!data.length) return;
    let csv = "S.No,Type,Name,Credit (In),Debit (Out)\n";
    data.forEach((item, i) => {
      csv += `${i + 1},${item.type},${item.name},${item.income || 0},${item.expense || 0}\n`;
    });
    csv += `\n,,Total,${summary.income},${summary.expense}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = "profit-loss.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const profit     = summary?.profit ?? ((summary?.income || 0) - (summary?.expense || 0));
  const isProfit   = profit >= 0;
  const selectedT  = tournaments.find((t) => String(t.id) === String(tournamentId));

  /* ── Sort header cell ── */
  const SortTh = ({ col, label, right }) => (
    <th
      style={{
        ...s.th,
        textAlign: right ? "right" : "left",
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
      onClick={() => handleSort(col)}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        <span style={{ display: "flex", flexDirection: "column", gap: 0, opacity: 0.5 }}>
          {sortCol === col && sortDir === "asc"
            ? <ChevronUpIco size={10} />
            : sortCol === col && sortDir === "desc"
            ? <ChevronDownIco size={10} />
            : <span style={{ fontSize: 9, lineHeight: 1, color: T.textMuted }}>⇅</span>}
        </span>
      </span>
    </th>
  );

  return (
    <>
      {/* ── Responsive breakpoint style ── */}
      <style>{`
        @media (max-width: 640px) {
          .pl-header   { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
          .pl-toolbar  { flex-direction: column !important; align-items: stretch !important; gap: 8px !important; }
          .pl-stats    { flex-direction: column !important; }
          .pl-stat-card{ flex: 1 1 100% !important; }
          .pl-th-sno   { display: none !important; }
          .pl-td-sno   { display: none !important; }
          .pl-td-num   { font-size: 12px !important; }
          .pl-table    { font-size: 12px !important; }
          .pl-table th, .pl-table td { padding: 10px 12px !important; }
        }
        @media (max-width: 420px) {
          .pl-th-type  { display: none !important; }
          .pl-td-type  { display: none !important; }
        }
      `}</style>

      <div style={s.page}>
        <div style={s.card}>

          {/* ═══ HEADER ═══ */}
          <header style={s.header} className="pl-header">
            <div>
              <h1 style={s.h1}>Profit &amp; loss</h1>
              <p style={s.headerSub}>
                {selectedT ? selectedT.name : "Tournament financial summary"}
              </p>
            </div>
            <button style={{ ...s.btn, ...s.btnSuccess }} onClick={handleExportExcel}>
              <DownloadIco size={14} />
              Export CSV
            </button>
          </header>

          {/* ═══ TOOLBAR ═══ */}
          <div style={s.toolbar} className="pl-toolbar">
            <div style={s.filterGroup}>
              <span style={s.filterLabel}>Tournament</span>
              <select
                style={s.select}
                value={tournamentId}
                onChange={(e) => { setTournamentId(e.target.value); setTypeFilter(""); }}
              >
                <option value="">Select event…</option>
                {tournaments.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {uniqueTypes.length > 0 && (
              <div style={s.filterGroup}>
                <span style={s.filterLabel}>Filter type</span>
                <select
                  style={s.select}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">All types</option>
                  {uniqueTypes.map((tp) => (
                    <option key={tp} value={tp}>{tp}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* ═══ STAT CARDS ═══ */}
          {tournamentId && !loading && summary && (
            <div style={s.statsWrap} className="pl-stats">
              <StatCard
                label="Total credit"
                value={`₹${Number(summary.income || 0).toLocaleString("en-IN")}`}
                icon={<TrendUpIco size={16} />}
                color={T.green}
                bg={T.greenLight}
                border={T.greenBorder}
              />
              <StatCard
                label="Total debit"
                value={`₹${Number(summary.expense || 0).toLocaleString("en-IN")}`}
                icon={<TrendDownIco size={16} />}
                color={T.red}
                bg={T.redLight}
                border={T.redBorder}
              />
              <StatCard
                label={isProfit ? "Net profit" : "Net loss"}
                value={`₹${Math.abs(Number(profit)).toLocaleString("en-IN")}`}
                icon={<ScaleIco size={16} />}
                color={isProfit ? T.green : T.red}
                bg={isProfit ? T.greenLight : T.redLight}
                border={isProfit ? T.greenBorder : T.redBorder}
              />
            </div>
          )}

          {/* ═══ TABLE ═══ */}
          <div style={{ overflowX: "auto" }}>
            <table style={s.table} className="pl-table">
              <thead>
                <tr style={s.thead}>
                  <th style={s.th} className="pl-th-sno">#</th>
                  <th style={s.th} className="pl-th-type">Type</th>
                  <SortTh col="name" label="Name" />
                  <SortTh col="income" label="Credit (in)" right />
                  <SortTh col="expense" label="Debit (out)" right />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} style={s.stateMsg}>Syncing database…</td></tr>
                ) : !tournamentId ? (
                  <tr><td colSpan={5} style={s.stateMsg}>Select a tournament to load financial data</td></tr>
                ) : displayData.length === 0 ? (
                  <tr><td colSpan={5} style={s.stateMsg}>No transactions found for this selection</td></tr>
                ) : (
                  displayData.map((item, i) => {
                    const hasIncome  = parseFloat(item.income  || 0) > 0;
                    const hasExpense = parseFloat(item.expense || 0) > 0;
                    return (
                      <tr key={i} style={s.tr}
                        onMouseEnter={(e) => e.currentTarget.style.background = T.bgSub}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ ...s.td, color: T.textMuted, fontSize: 11 }} className="pl-td-sno">{i + 1}</td>
                        <td style={s.td} className="pl-td-type">
                          <TypePill type={item.type} />
                        </td>
                        <td style={s.td}>
                          <span style={{ fontWeight: 500, color: T.textPrimary, fontSize: 13 }}>{item.name}</span>
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }} className="pl-td-num">
                          {hasIncome ? (
                            <span style={s.amountCredit}>
                              ₹{parseFloat(item.income).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          ) : (
                            <span style={{ color: T.textMuted }}>—</span>
                          )}
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }} className="pl-td-num">
                          {hasExpense ? (
                            <span style={s.amountDebit}>
                              ₹{parseFloat(item.expense).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          ) : (
                            <span style={{ color: T.textMuted }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {/* ── Totals row ── */}
              {tournamentId && !loading && displayData.length > 0 && (
                <tfoot>
                  <tr style={s.tfootRow}>
                    <td style={{ ...s.td, ...s.tfootCell }} className="pl-td-sno"></td>
                    <td style={{ ...s.td, ...s.tfootCell }} className="pl-td-type"></td>
                    <td style={{ ...s.td, ...s.tfootCell }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.textSec, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Total
                      </span>
                    </td>
                    <td style={{ ...s.td, ...s.tfootCell, textAlign: "right" }}>
                      <span style={{ ...s.amountCredit, fontSize: 14, fontWeight: 700 }}>
                        ₹{Number(summary?.income || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td style={{ ...s.td, ...s.tfootCell, textAlign: "right" }}>
                      <span style={{ ...s.amountDebit, fontSize: 14, fontWeight: 700 }}>
                        ₹{Number(summary?.expense || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* ═══ FOOTER ═══ */}
          {tournamentId && !loading && displayData.length > 0 && (
            <footer style={s.footer}>
              <span style={s.footerStat}>
                {displayData.length} transaction{displayData.length !== 1 ? "s" : ""}
                {typeFilter ? ` · filtered by "${typeFilter}"` : ""}
              </span>
              {isProfit ? (
                <span style={{ fontSize: 12, fontWeight: 600, color: T.green, background: T.greenLight, border: `1px solid ${T.greenBorder}`, borderRadius: 4, padding: "3px 10px" }}>
                  Profitable
                </span>
              ) : (
                <span style={{ fontSize: 12, fontWeight: 600, color: T.red, background: T.redLight, border: `1px solid ${T.redBorder}`, borderRadius: 4, padding: "3px 10px" }}>
                  Running at loss
                </span>
              )}
            </footer>
          )}
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const s = {
  page: {
    background: "#f1f5f9",
    minHeight: "100vh",
    padding: "2rem 1rem",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    maxWidth: 980,
    margin: "0 auto",
    background: T.bg,
    border: `1px solid ${T.border}`,
    borderRadius: T.rLg,
    boxShadow: T.shadowMd,
    overflow: "hidden",
  },

  /* header */
  header: {
    padding: "1.25rem 1.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${T.border}`,
    gap: "1rem",
    flexWrap: "wrap",
  },
  h1: {
    fontSize: 15,
    fontWeight: 600,
    color: T.textPrimary,
    margin: 0,
    letterSpacing: "-0.01em",
  },
  headerSub: {
    fontSize: 12,
    color: T.textMuted,
    margin: "3px 0 0",
  },

  /* buttons */
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 13px",
    fontSize: 12,
    fontWeight: 500,
    borderRadius: T.r,
    border: `1px solid ${T.border}`,
    background: T.bg,
    color: T.textPrimary,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnSuccess: {
    background: "#f0fdf4",
    color: T.green,
    border: `1px solid ${T.greenBorder}`,
  },

  /* toolbar */
  toolbar: {
    padding: "0.75rem 1.5rem",
    display: "flex",
    alignItems: "center",
    gap: 16,
    background: T.bgSub,
    borderBottom: `1px solid ${T.border}`,
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  filterLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: T.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    whiteSpace: "nowrap",
  },
  select: {
    border: `1px solid ${T.border}`,
    borderRadius: T.r,
    padding: "7px 10px",
    fontSize: 12,
    background: T.bg,
    color: T.textPrimary,
    outline: "none",
    minWidth: 180,
  },

  /* stat cards */
  statsWrap: {
    display: "flex",
    gap: 12,
    padding: "1.25rem 1.5rem",
    borderBottom: `1px solid ${T.border}`,
    flexWrap: "wrap",
  },

  /* table */
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 480,
  },
  thead: {
    background: T.bgSub,
  },
  th: {
    padding: "10px 1.5rem",
    fontSize: 11,
    fontWeight: 600,
    color: T.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: `1px solid ${T.border}`,
    textAlign: "left",
  },
  tr: {
    transition: "background 0.1s",
  },
  td: {
    padding: "12px 1.5rem",
    fontSize: 13,
    borderBottom: `1px solid #f8fafc`,
    verticalAlign: "middle",
    color: T.textPrimary,
  },
  amountCredit: {
    fontSize: 13,
    fontWeight: 600,
    color: T.green,
    fontVariantNumeric: "tabular-nums",
  },
  amountDebit: {
    fontSize: 13,
    fontWeight: 600,
    color: T.red,
    fontVariantNumeric: "tabular-nums",
  },
  tfootRow: {
    background: T.bgSub,
    borderTop: `2px solid ${T.border}`,
  },
  tfootCell: {
    fontWeight: 700,
    borderBottom: "none",
    paddingTop: 14,
    paddingBottom: 14,
  },
  stateMsg: {
    textAlign: "center",
    padding: "4rem 1rem",
    color: T.textMuted,
    fontSize: 13,
    fontStyle: "italic",
  },

  /* footer */
  footer: {
    padding: "0.875rem 1.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTop: `1px solid ${T.border}`,
    flexWrap: "wrap",
    gap: 8,
  },
  footerStat: {
    fontSize: 12,
    color: T.textMuted,
    fontWeight: 500,
  },
};

export default ProfitLoss;
